/**
 * The bytes under both colour spaces: `#rrggbb` in and out, and the one cylinder→RGB step HSL and
 * HSV share.
 *
 * `contrast.ts` beside it works in HSL because that is the right space for "make this readable",
 * and `/konto`'s `model/colorPicker.ts` works in HSV because that is what the handoff draws. The two
 * stay apart for that reason — but the sextant walk under them is the same six-branch table, and
 * it was written out twice, as were the channel parse and the two-digit format. One arithmetic
 * error in one copy is a hue that disagrees with itself between the plane and the AA chip.
 */

export interface Rgb {
  /** 0–255. */
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

export function clamp01(value: number): number {
  return Math.min(Math.max(value, 0), 1);
}

/** `#rrggbb` → channels. Everything in this module is an opaque six-digit hex. */
export function parseHex(hex: string): Rgb {
  const body = hex.replace('#', '');
  return {
    r: parseInt(body.slice(0, 2), 16),
    g: parseInt(body.slice(2, 4), 16),
    b: parseInt(body.slice(4, 6), 16),
  };
}

/** Channels → `#rrggbb`, rounded and clamped. */
export function toHex({ r, g, b }: Rgb): string {
  const pair = (value: number) =>
    Math.round(Math.min(Math.max(value, 0), 255))
      .toString(16)
      .padStart(2, '0');
  return `#${pair(r)}${pair(g)}${pair(b)}`;
}

/**
 * The six-sextant step both cylinders end in: a hue in degrees, the chroma `c` the space computed,
 * and the offset `m` that lifts the result back to its lightness or value. HSL and HSV differ only
 * in how they arrive at those two numbers.
 */
export function cylinderToRgb(hue: number, c: number, m: number): Rgb {
  const hp = (((hue % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const [r, g, b] =
    hp < 1
      ? [c, x, 0]
      : hp < 2
        ? [x, c, 0]
        : hp < 3
          ? [0, c, x]
          : hp < 4
            ? [0, x, c]
            : hp < 5
              ? [x, 0, c]
              : [c, 0, x];
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}
